import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  DatePicker,
  Select,
  Checkbox,
  Button,
  InputNumber,
  Popover,
} from "antd";
import { max, uniqBy } from "lodash";
import moment from "moment";
import config from "../../config";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import {
  PlusOutlined,
  MinusOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import "./shuttle.css";
const { Option } = Select;

// Temporary front-end patch: Sahastradhara <-> Gauchar is sold out for these
// dates (both directions). To be moved to the backend.
const SOLD_OUT_DATES = [
  "28-05-2026",
  "29-05-2026",
  "30-05-2026",
  "31-05-2026",
  "01-06-2026",
  "02-06-2026",
  "03-06-2026",
  "04-06-2026",
  "05-06-2026",
  "06-06-2026",
  "07-06-2026",
  "08-06-2026",
  "09-06-2026",
  "10-06-2026",
  "11-06-2026",
  "12-06-2026",
  "13-06-2026",
  "14-06-2026",
];

const isSahastradharaGaucharSoldOut = (schedule, date) => {
  if (!date) return false;
  const halts = schedule.path.halts;
  const routeNames = [
    halts[0].name.toLowerCase(),
    halts[halts.length - 1].name.toLowerCase(),
  ];
  const isRoute =
    routeNames.some((n) => n.includes("sahastradhara")) &&
    routeNames.some((n) => n.includes("gauchar"));
  return isRoute && SOLD_OUT_DATES.includes(date.format("DD-MM-YYYY"));
};

// Temporary front-end patch: Gauchar <-> Badrinath is sold out for these dates.
const BADRINATH_SOLD_OUT_DATES = [
  "28-05-2026",
  "29-05-2026",
  "30-05-2026",
  "31-05-2026",
  "01-06-2026",
  "02-06-2026",
  "03-06-2026",
  "04-06-2026",
  "05-06-2026",
  "06-06-2026",
  "07-06-2026",
  "08-06-2026",
  "09-06-2026",
  "10-06-2026",
  "11-06-2026",
  "12-06-2026",
  "13-06-2026",
  "14-06-2026",
  "15-06-2026",
  "16-06-2026",
  "17-06-2026",
  "18-06-2026",
  "19-06-2026",
  "20-06-2026",
];

const isGaucharBadrinathSoldOut = (schedule, date) => {
  if (!date) return false;
  const halts = schedule.path.halts;
  const routeNames = [
    halts[0].name.toLowerCase(),
    halts[halts.length - 1].name.toLowerCase(),
  ];
  const isRoute =
    routeNames.some((n) => n.includes("gauchar")) &&
    routeNames.some((n) => n.includes("badrinath"));
  return isRoute && BADRINATH_SOLD_OUT_DATES.includes(date.format("DD-MM-YYYY"));
};

// Morning slot (09:00 AM) for Sahastradhara <-> Joshiyara is sold out.
const isJoshiyaraMorningSoldOut = (schedule) => {
  const halts = schedule.path.halts;
  const routeNames = [
    halts[0].name.toLowerCase(),
    halts[halts.length - 1].name.toLowerCase(),
  ];
  const isRoute =
    routeNames.some((n) => n.includes("sahastradhara")) &&
    routeNames.some((n) => n.includes("joshiyara"));
  const isMorningSlot =
    Array.isArray(schedule.arrivalTime) &&
    schedule.arrivalTime[0] === "09:00";
  return isRoute && isMorningSlot;
};

const hasReturnData = (direction) => {
  if (!direction) return false;
  return (
    direction.title ||
    direction.slot ||
    (direction.arrivalTime &&
      Array.isArray(direction.arrivalTime) &&
      direction.arrivalTime.length > 0)
  );
};

const Shuttle = ({ schedules, setTermsnConditions, setLoading, isSoldOut }) => {
  const [form, setForm] = useState({
    from: "",
    to: "",
    date: "",
    isTermsAndConditionsCheckedIndex: -1,
    seats: 1,
  });
  const history = useHistory();
  const location = useLocation();

  const [validSchedules, setValidSchedules] = useState([]);
  const [validDates, setValidDates] = useState([]);
  const [fromLocations, setFromLocations] = useState([]);
  const [toLocations, setToLocations] = useState([]);
  const [errorsMap, setErrorsMap] = useState({});
  const [popoverVisible, setPopoverVisible] = useState({});

  useEffect(() => {
    setFromLocations(
      uniqBy(
        schedules.map((s) => ({
          name: s.path.halts[0].name,
          id: s.path.halts[0]._id,
        })),
        "id"
      ).sort((a, b) => a.name.localeCompare(b.name))
    );
  }, [schedules]);

  useEffect(() => {
    // find schedules with from location as the first halt
    const validSchedules = schedules.filter(
      (s) => s.path.halts[0]._id === form.from
    );

    // set the to locations
    setToLocations(
      uniqBy(
        validSchedules.map((s) => ({
          name: s.path.halts[s.path.halts.length - 1].name,
          id: s.path.halts[s.path.halts.length - 1]._id,
        })),
        "id"
      ).sort((a, b) => a.name.localeCompare(b.name))
    );
  }, [form.from]);

  useEffect(() => {
    // find schedules with the selected from and to locations
    const validSchedules = schedules.filter(
      (s) =>
        s.path.halts[0]._id === form.from &&
        s.path.halts[s.path.halts.length - 1]._id === form.to
    );

    // set the dates - from the effectiveFromDate to the effectiveToDate for all the valid schedules
    // remove dates that are in the offDates array and are offDaysOfTheWeek
    const dates = [];
    validSchedules.forEach((s) => {
      const [hour, minute] = s.arrivalTime[0].split(":");

      for (
        let date = moment(s.effectiveFromDate)
          .utcOffset(5.5)
          .hour(hour)
          .minute(minute);
        date <= moment(s.effectiveToDate).utcOffset(5.5).endOf("day");
        date.add(1, "day")
      ) {
        if (
          !s.offDates.includes(date.format("MM/DD/YYYY")) &&
          !s.offDaysOfTheWeek.includes(date.day()) &&
          moment(date)
            .subtract(s.minutesBeforeLastFlyTodayBooking, "minutes")
            .isAfter(moment())
        ) {
          dates.push(date.format("DD-MM-YYYY"));
        }
      }

      // sort the dates
      dates.sort((a, b) => {
        return dayjs(a, "DD-MM-YYYY").diff(dayjs(b, "DD-MM-YYYY"));
      });
      setValidDates(dates);
    });
  }, [form.from, form.to]);

  useEffect(() => {
    if (form.date && form.from && form.to) {
      const selectedDate = moment(form.date.toISOString()).utcOffset(5.5);
      const validSchedules = schedules.filter((s) => {
        const fromDate = moment(s.effectiveFromDate).utcOffset(5.5);
        const toDate = moment(s.effectiveToDate).utcOffset(5.5);
        const day = selectedDate.day();

        if (
          s.path.halts[0]._id === form.from &&
          s.path.halts[s.path.halts.length - 1]._id === form.to
        ) {
          return (
            s.path.halts[0]._id === form.from &&
            s.path.halts[s.path.halts.length - 1]._id === form.to &&
            validDates.includes(selectedDate.format("DD-MM-YYYY")) &&
            !s.offDaysOfTheWeek.includes(day) &&
            !s.offDates.includes(selectedDate.format("MM/DD/YYYY")) &&
            selectedDate.isSameOrAfter(fromDate) &&
            selectedDate.isSameOrBefore(toDate)
          );
        }

        return false;
      });
      setValidSchedules(validSchedules);
    } else {
      setValidSchedules([]);
    }
  }, [form.date, form.from, form.to]);

  const isFormValid = () => {
    return (
      form.from &&
      form.to &&
      form.date &&
      form.isTermsAndConditionsCheckedIndex !== -1 &&
      form.seats > 0
    );
  };

  const checkSeatAvailability = (selectedScheduleId) => {
    const valid = isFormValid();

    if (valid) {
      setLoading(true);
      setErrorsMap({});
      axios
        .get(
          `${
            config.baseUrl
          }v2/schedules/availability?travelDate=${form.date.format(
            "MM/DD/YYYY"
          )}&scheduleId=${selectedScheduleId}`
        )
        .then((res) => {
          if (res.status === 200) {
            if (res.data.availableSeats <= 0) {
              setErrorsMap({
                [selectedScheduleId]: "No seats available",
              });
              return;
            }

            if (res.data.availableSeats < form.seats) {
              setErrorsMap({
                [selectedScheduleId]: `Only ${res.data.availableSeats} seats available`,
              });
              return;
            }

            const selectedScheduleObj = validSchedules.find(
              (s) => s._id === selectedScheduleId
            );

            // Preserve utm_source from current URL
            const params = new URLSearchParams(location.search);
            const utmSource = params.get("utm_source");
            const search = utmSource
              ? `?utm_source=${encodeURIComponent(utmSource)}`
              : "";

            history.push({
              pathname: "/booking",
              search: search,
              state: {
                selectedScheduleObj,
                date: form.date.format("MM/DD/YYYY"),
                selectedDateFormatted: form.date.format("MM/DD/YYYY"),
                seats: res.data.availableSeats,
                selectedSeats: form.seats,
              },
            });
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const maxSeats = max(schedules.map((s) => s.availableSeats));

  return (
    <div
      className="shuttle-main-container"
      style={{ border: "1px solid #ccc", padding: "20px" }}
    >
      <div className="shuttle-form-container">
        <div className="shuttle-form-from">
          <div>
            <label style={{ fontSize: "16px" }}>From</label>
          </div>
          <Select
            value={form.from}
            placeholder="Select From Location"
            size="large"
            style={{ width: "100%" }}
            onChange={(value) => {
              setForm((prev) => ({ ...prev, from: value, to: "", date: "" }));
            }}
          >
            {fromLocations.map((l) => (
              <Option key={l.id} value={l.id}>
                {l.name}
              </Option>
            ))}
          </Select>
        </div>

        <div className="shuttle-form-to">
          <div>
            <label style={{ fontSize: "16px" }}>To</label>
          </div>
          <Select
            value={form.to}
            size="large"
            style={{ width: "100%" }}
            disabled={!form.from}
            onChange={(value) => {
              setForm((prev) => ({ ...prev, to: value }));
            }}
          >
            {toLocations.map((l) => (
              <Option key={l.id} value={l.id}>
                {l.name}
              </Option>
            ))}
          </Select>
        </div>

        <div className="shuttle-form-date">
          <div>
            <label style={{ fontSize: "16px" }}>Departure Date</label>
          </div>
          <DatePicker
            format="Do MMM YYYY"
            value={form.date}
            size="large"
            style={{ width: "100%" }}
            disabled={!validDates.length}
            disabledDate={(current) => {
              return !validDates.includes(current.format("DD-MM-YYYY"));
            }}
            defaultPickerValue={dayjs(validDates[0], "DD-MM-YYYY")}
            onChange={(value) => {
              setForm((prev) => ({ ...prev, date: value }));
            }}
            superNextIcon={<></>}
            superPrevIcon={<></>}
            minDate={dayjs(validDates[0], "DD-MM-YYYY")}
            maxDate={dayjs(validDates[validDates.length - 1], "DD-MM-YYYY")}
          />
        </div>
      </div>
      <div className="shuttle-form-container">
        <div className="shuttle-form-seats">
          <div>
            <label style={{ fontSize: "16px" }}>Seats</label>
          </div>
          <Button
            type="primary"
            shape="circle"
            icon={<MinusOutlined />}
            onClick={() =>
              setForm((prev) => ({ ...prev, seats: prev.seats - 1 }))
            }
            disabled={form.seats <= 1 || !form.date}
          />
          <span
            style={{
              fontSize: "16px",
              margin: "0 10px",
              width: "15px",
              display: "inline-block",
              textAlign: "center",
            }}
          >
            {form.seats}
          </span>
          <Button
            type="primary"
            shape="circle"
            icon={<PlusOutlined />}
            onClick={() =>
              setForm((prev) => ({ ...prev, seats: prev.seats + 1 }))
            }
            disabled={form.seats >= maxSeats || !form.date}
          />
        </div>
      </div>

      {validSchedules.map((s, index) => {
        const isRouteSoldOut =
          isSoldOut ||
          isSahastradharaGaucharSoldOut(s, form.date) ||
          isGaucharBadrinathSoldOut(s, form.date) ||
          isJoshiyaraMorningSoldOut(s);
        return (
          <div
            className="shuttle-form-schedules-container"
            key={s._id}
            style={{ padding: "20px" }}
          >
            <div
              className="schedule-header-container"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "20px",
                gap: "20px",
              }}
            >
              <div className="schedule-details">
                <div className="schedule-details-path-name">{s.path.name}</div>
                {s.return &&
                (hasReturnData(s.return.up) || hasReturnData(s.return.down)) ? (
                  <div className="schedule-return-details">
                    {hasReturnData(s.return.up) && (
                      <div style={{ marginBottom: "15px" }}>
                        <div className="schedule-details-path-name text-[16px]">
                          {s.return.up.title}
                        </div>
                        <div className="schedule-details-schedule-name">
                          {s.return.up.slot}
                        </div>
                      </div>
                    )}
                    {hasReturnData(s.return.down) && (
                      <div>
                        <div className="schedule-details-path-name text-[16px]">
                          {s.return.down.title}
                        </div>
                        <div className="schedule-details-schedule-name">
                          {s.return.down.slot}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="schedule-details-schedule-name">
                      {s.name}
                    </div>
                  </>
                )}
              </div>
              <div
                className="shuttle-form-price-breakdown-container"
                style={{ width: "auto" }}
              >
                {(() => {
                  const basePriceItem = s.priceLineItems.find(
                    (p) => p.key === "seatCharges"
                  );
                  const gstItem = s.priceLineItems.find(
                    (item) => item.key === "seatChargesGst"
                  );

                  const hasAgeBasedPrices =
                    basePriceItem?.ageBasedPrices &&
                    Array.isArray(basePriceItem.ageBasedPrices) &&
                    basePriceItem.ageBasedPrices.length > 0;

                  // Check if early bird discount is active
                  const earlyBirdDiscount = s?.earlyBirdDiscount;
                  const isEarlyBirdActive =
                    earlyBirdDiscount?.enabled === true &&
                    earlyBirdDiscount?.bookingCutoffDate &&
                    moment().isSameOrBefore(
                      moment(earlyBirdDiscount.bookingCutoffDate)
                    );

                  const originalPrice = earlyBirdDiscount?.originalPrice;

                  let displayPrice = 0;
                  let originalDisplayPrice = 0;

                  if (hasAgeBasedPrices) {
                    const adultAgePrice = basePriceItem.ageBasedPrices.find(
                      (agePrice) =>
                        agePrice.minAge === 13 && agePrice.maxAge === 59
                    );

                    if (adultAgePrice) {
                      let gstPrice = 0;
                      if (
                        gstItem?.ageBasedPrices &&
                        Array.isArray(gstItem.ageBasedPrices)
                      ) {
                        const matchingGst = gstItem.ageBasedPrices.find(
                          (gstAgePrice) =>
                            gstAgePrice.minAge === 13 &&
                            gstAgePrice.maxAge === 59
                        );
                        gstPrice =
                          matchingGst?.price || adultAgePrice.price * 0.05;
                      } else {
                        gstPrice = adultAgePrice.price * 0.05;
                      }
                      displayPrice = adultAgePrice.price + gstPrice;
                    } else {
                      const firstAgePrice = basePriceItem.ageBasedPrices[0];
                      let gstPrice = 0;
                      if (
                        gstItem?.ageBasedPrices &&
                        Array.isArray(gstItem.ageBasedPrices)
                      ) {
                        const matchingGst = gstItem.ageBasedPrices.find(
                          (gstAgePrice) =>
                            gstAgePrice.minAge === firstAgePrice.minAge &&
                            gstAgePrice.maxAge === firstAgePrice.maxAge
                        );
                        gstPrice =
                          matchingGst?.price || firstAgePrice.price * 0.05;
                      } else {
                        gstPrice = firstAgePrice.price * 0.05;
                      }
                      displayPrice = firstAgePrice.price + gstPrice;
                    }
                  } else if (basePriceItem?.price !== undefined) {
                    let gstPrice = 0;
                    if (gstItem?.price !== undefined) {
                      gstPrice = gstItem.price;
                    } else {
                      gstPrice = basePriceItem.price * 0.05;
                    }
                    displayPrice = basePriceItem.price + gstPrice;
                  }

                  // Store original price before discount
                  originalDisplayPrice = displayPrice;

                  // Apply early bird discount if active
                  if (isEarlyBirdActive && earlyBirdDiscount?.discountAmount) {
                    displayPrice = Math.max(0, displayPrice - earlyBirdDiscount.discountAmount);
                  }

                  const tooltipContent = hasAgeBasedPrices ? (
                    <div className="price-tooltip-content">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "6px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#333",
                          }}
                        >
                          Price Breakdown
                        </div>
                        <Button
                          type="text"
                          icon={<CloseOutlined />}
                          className="price-tooltip-close-btn"
                          onClick={() =>
                            setPopoverVisible({
                              ...popoverVisible,
                              [s._id]: false,
                            })
                          }
                          style={{
                            padding: 0,
                            height: "auto",
                            minWidth: "auto",
                            display: "none",
                          }}
                        />
                      </div>
                      {basePriceItem.ageBasedPrices.map((agePrice) => {
                        const ageRange =
                          agePrice.maxAge === null
                            ? `${agePrice.minAge}+ years`
                            : agePrice.minAge === agePrice.maxAge
                            ? `${agePrice.minAge} years`
                            : `${agePrice.minAge}-${agePrice.maxAge} years`;

                        let gstPrice = 0;
                        if (
                          gstItem?.ageBasedPrices &&
                          Array.isArray(gstItem.ageBasedPrices)
                        ) {
                          const matchingGst = gstItem.ageBasedPrices.find(
                            (gstAgePrice) =>
                              gstAgePrice.minAge === agePrice.minAge &&
                              gstAgePrice.maxAge === agePrice.maxAge
                          );
                          gstPrice =
                            matchingGst?.price || agePrice.price * 0.05;
                        } else {
                          gstPrice = agePrice.price * 0.05;
                        }

                        const totalPrice = agePrice.price + gstPrice;

                        const isLastAgeGroup =
                          agePrice._id ===
                          basePriceItem.ageBasedPrices[
                            basePriceItem.ageBasedPrices.length - 1
                          ]._id;

                        return (
                          <div
                            key={agePrice._id}
                            style={{
                              backgroundColor: "#fafafa",
                              padding: "8px 10px",
                              borderRadius: "6px",
                              marginBottom: isLastAgeGroup ? "0" : "6px",
                              border: "1px solid #e8e8e8",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                fontSize: "13px",
                                fontWeight: 600,
                                marginBottom: "4px",
                                color: "#333",
                              }}
                            >
                              <CheckCircleOutlined
                                style={{ color: "#005299", fontSize: "12px" }}
                              />
                              <span>{ageRange}</span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginBottom: "2px",
                                fontSize: "12px",
                                color: "#666",
                              }}
                            >
                              <span>Base Price:</span>
                              <span>₹ {agePrice.price.toLocaleString()}</span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "12px",
                                color: "#666",
                                marginBottom: "4px",
                              }}
                            >
                              <span>5% GST:</span>
                              <span>
                                ₹ {Math.round(gstPrice).toLocaleString()}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#005299",
                                paddingTop: "4px",
                                borderTop: "1px solid #e8e8e8",
                              }}
                            >
                              <span>Total:</span>
                              <span>
                                ₹ {Math.round(totalPrice).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      {isEarlyBirdActive && earlyBirdDiscount?.discountAmount && (
                        <div
                          style={{
                            backgroundColor: "#fffbf0",
                            padding: "8px 10px",
                            borderRadius: "6px",
                            marginTop: "8px",
                            border: "1px solid #f59e0b",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: "12px",
                              color: "#d97706",
                              marginBottom: "4px",
                            }}
                          >
                            <span>Early Bird Discount:</span>
                            <span>
                              -₹ {earlyBirdDiscount.discountAmount.toLocaleString()}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: "13px",
                              fontWeight: 600,
                              color: "#005299",
                              paddingTop: "4px",
                              borderTop: "1px solid #f59e0b",
                            }}
                          >
                            <span>Final Price:</span>
                            <span>
                              ₹ {Math.round(displayPrice).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="price-tooltip-content">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "6px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#333",
                          }}
                        >
                          Price Breakdown
                        </div>
                        <Button
                          type="text"
                          icon={<CloseOutlined />}
                          className="price-tooltip-close-btn"
                          onClick={() =>
                            setPopoverVisible({
                              ...popoverVisible,
                              [s._id]: false,
                            })
                          }
                          style={{
                            padding: 0,
                            height: "auto",
                            minWidth: "auto",
                            display: "none",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          backgroundColor: "#fafafa",
                          padding: "8px 10px",
                          borderRadius: "6px",
                          border: "1px solid #e8e8e8",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "2px",
                            fontSize: "12px",
                            color: "#666",
                          }}
                        >
                          <span>Base Price:</span>
                          <span>
                            ₹ {basePriceItem?.price?.toLocaleString() || "0"}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "12px",
                            color: "#666",
                            marginBottom: "4px",
                          }}
                        >
                          <span>5% GST:</span>
                          <span>
                            ₹{" "}
                            {Math.round(
                              basePriceItem?.price
                                ? basePriceItem.price * 0.05
                                : 0
                            ).toLocaleString()}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#005299",
                            paddingTop: "4px",
                            borderTop: "1px solid #e8e8e8",
                          }}
                        >
                          <span>Total:</span>
                          <span>
                            ₹ {Math.round(originalDisplayPrice).toLocaleString()}
                          </span>
                        </div>
                        {isEarlyBirdActive && earlyBirdDiscount?.discountAmount && (
                          <>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "12px",
                                color: "#d97706",
                                marginTop: "6px",
                                paddingTop: "6px",
                                borderTop: "1px solid #e8e8e8",
                              }}
                            >
                              <span>Early Bird Discount:</span>
                              <span>
                                -₹ {earlyBirdDiscount.discountAmount.toLocaleString()}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#005299",
                                marginTop: "4px",
                                paddingTop: "4px",
                                borderTop: "1px solid #e8e8e8",
                              }}
                            >
                              <span>Final Price:</span>
                              <span>
                                ₹ {Math.round(displayPrice).toLocaleString()}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );

                  if (isEarlyBirdActive && originalPrice) {
                    return (
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "baseline",
                          gap: "6px",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            color: "#005299",
                            fontSize: "24px",
                            fontWeight: 700,
                            lineHeight: 1,
                          }}
                        >
                          ₹ {Math.round(displayPrice).toLocaleString()}
                        </span>
                        <span
                          style={{
                            textDecoration: "line-through",
                            color: "#999",
                            fontSize: "16px",
                            fontWeight: 400,
                            lineHeight: 1,
                          }}
                        >
                          ₹ {Math.round(originalPrice).toLocaleString()}
                        </span>
                        {hasAgeBasedPrices && (
                          <span
                            style={{
                              color: "#666",
                              fontSize: "13px",
                              fontWeight: 400,
                            }}
                          >
                            onwards
                          </span>
                        )}
                        <Popover
                          content={tooltipContent}
                          title={null}
                          trigger="click"
                          placement="right"
                          visible={popoverVisible[s._id]}
                          onVisibleChange={(visible) =>
                            setPopoverVisible({
                              ...popoverVisible,
                              [s._id]: visible,
                            })
                          }
                        >
                          <Button
                            type="text"
                            icon={<InfoCircleOutlined />}
                            style={{
                              color: "#005299",
                              padding: "2px 4px",
                              height: "auto",
                              border: "none",
                              fontSize: "14px",
                              marginLeft: "4px",
                            }}
                          />
                        </Popover>
                      </div>
                    );
                  }

                  return (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "baseline",
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          color: "#005299",
                          fontSize: "24px",
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        ₹ {Math.round(displayPrice).toLocaleString()}
                      </span>
                      {hasAgeBasedPrices && (
                        <span
                          style={{
                            color: "#666",
                            fontSize: "13px",
                            fontWeight: 400,
                          }}
                        >
                          onwards
                        </span>
                      )}
                      <Popover
                        content={tooltipContent}
                        title={null}
                        trigger="click"
                        placement="right"
                        visible={popoverVisible[s._id]}
                        onVisibleChange={(visible) =>
                          setPopoverVisible({
                            ...popoverVisible,
                            [s._id]: visible,
                          })
                        }
                      >
                        <Button
                          type="text"
                          icon={<InfoCircleOutlined />}
                          style={{
                            color: "#005299",
                            padding: "2px 4px",
                            height: "auto",
                            border: "none",
                            fontSize: "14px",
                            marginLeft: "4px",
                          }}
                        />
                      </Popover>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div
              className="shuttle-form-book-now"
              style={{ marginTop: "16px" }}
            >
              <div
                className="shuttle-form-book-now-wrapper"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  flexWrap: "wrap",
                }}
              >
                {s.path.locationLink && (
                  <div style={{ width: "100%", marginBottom: "12px" }}>
                    <a
                      href={s.path.locationLink}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontSize: "13px",
                        color: "#005299",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        textDecoration: "none",
                        lineHeight: "1.5",
                        display: "inline-block",
                      }}
                    >
                      <EnvironmentOutlined
                        style={{ fontSize: "14px", marginRight: "4px" }}
                      />
                      <span>View boarding point</span>
                    </a>
                  </div>
                )}
                <div
                  className="shuttle-form-booking-actions"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: "10px",
                    width: "100%",
                  }}
                >
                  {errorsMap[s._id] && (
                    <div
                      className="shuttle-form-error-message"
                      style={{
                        color: "red",
                        fontSize: "13px",
                        textAlign: "right",
                        width: "100%",
                      }}
                    >
                      {errorsMap[s._id]}
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: "8px",
                      width: "100%",
                    }}
                  >
                    <Button
                      type="primary"
                      title={
                        isRouteSoldOut
                          ? "Sold out"
                          : form.isTermsAndConditionsCheckedIndex === -1 ||
                            form.isTermsAndConditionsCheckedIndex !== index
                          ? "Please accept the terms and conditions"
                          : "Book Now"
                      }
                      disabled={
                        isRouteSoldOut ||
                        form.isTermsAndConditionsCheckedIndex === -1 ||
                        form.isTermsAndConditionsCheckedIndex !== index
                      }
                      onClick={() => checkSeatAvailability(s._id)}
                      size="large"
                    >
                      {isRouteSoldOut
                        ? "Sold out"
                        : `Book ${form.seats} seat${
                            form.seats > 1 ? "s" : ""
                          } Now`}
                    </Button>
                    {!isRouteSoldOut && (
                      <Checkbox
                        checked={
                          form.isTermsAndConditionsCheckedIndex === index
                        }
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            isTermsAndConditionsCheckedIndex:
                              prev.isTermsAndConditionsCheckedIndex === index
                                ? -1
                                : index,
                          }))
                        }
                      >
                        <span
                          style={{
                            whiteSpace: "nowrap",
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          I agree to the{" "}
                          <Button
                            type="link"
                            style={{
                              padding: 0,
                              height: "auto",
                              lineHeight: "inherit",
                            }}
                            onClick={() => setTermsnConditions(s.ticketNotes)}
                          >
                            Terms and conditions
                          </Button>
                        </span>
                      </Checkbox>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

Shuttle.propTypes = {
  schedules: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      path: PropTypes.shape({
        halts: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            lat: PropTypes.string.isRequired,
            lng: PropTypes.string.isRequired,
            isAirport: PropTypes.bool.isRequired,
            extraChargesCharter: PropTypes.number.isRequired,
            isActive: PropTypes.bool.isRequired,
            createdAt: PropTypes.string.isRequired,
          })
        ).isRequired,
      }).isRequired,
      arrivalTime: PropTypes.arrayOf(PropTypes.string).isRequired,
      type: PropTypes.string.isRequired,
      effectiveFromDate: PropTypes.string.isRequired,
      effectiveToDate: PropTypes.string.isRequired,
      offDaysOfTheWeek: PropTypes.arrayOf(PropTypes.number).isRequired,
      offDates: PropTypes.arrayOf(PropTypes.string).isRequired,
      ticketNotes: PropTypes.arrayOf(PropTypes.string).isRequired,
      priceLineItems: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          price: PropTypes.number.isRequired,
          key: PropTypes.string.isRequired,
        })
      ).isRequired,
      availableSeats: PropTypes.number.isRequired,
      advancePayment: PropTypes.bool.isRequired,
      minutesBeforeLastFlyTodayBooking: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired,
      createdAt: PropTypes.string.isRequired,
    })
  ).isRequired,
  setLoading: PropTypes.func.isRequired,
  setTermsnConditions: PropTypes.func.isRequired,
  isSoldOut: PropTypes.bool,
};
export default Shuttle;
